/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PillsInput,
  Pill,
  useTree,
  PillsInputProps,
  Popover,
  HoverCard,
  Text,
  ActionIcon,
  OverflowList,
  Stack,
} from "@mantine/core"
import { getSelectedNodes } from "./getSelectedNodes"
import { TreeRenderer, TreeRendererProps } from "./index"
import { IconSelector, IconX } from "@tabler/icons-react"
import { ScrollContainer } from "../scroll-container"
import { findMaxPillCountBasedOnValueLabelLength } from "./findMaxPillCountBasedOnValueLabelLength"
import { debugLog } from "@/utils/debugLog"
import { TreeNode } from "../../@client/Theasoft_ApiServer__Utils_TreeBase"

function arraysAreEqual<T>(a: T[], b: T[]) {
  if (a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}

function getCheckedLeafValues<T>(
  treeNodes: TreeNode<T>[],
  selectedValues: string[]
) {
  const selectedValueSet = new Set(selectedValues)
  const checkedLeafValues = new Set<string>()

  function addLeafValues(node: TreeNode<T>) {
    if (node.children.length === 0) {
      checkedLeafValues.add(node.value)
      return
    }

    node.children.forEach(addLeafValues)
  }

  function traverse(node: TreeNode<T>) {
    if (selectedValueSet.has(node.value)) {
      addLeafValues(node)
      return
    }

    node.children.forEach(traverse)
  }

  treeNodes.forEach(traverse)
  return [...checkedLeafValues]
}

export interface TreePillsInputProps<T>
  extends
    PillsInputProps,
    Pick<
      TreeRendererProps<T, null>,
      | "tree"
      | "initialCheckedState"
      | "initialExpandedState"
      | "onCheckedStateChange"
      | "nodeRenderer"
    > {
  // limits the maximum number of pills shown based on number of pills
  maxPills?: number
  // limits the maximum number of pills shown based on character lenght of items (+ spaces)
  maxChars?: number
  // hides pills that do not fit into the input width and shows a +N overflow pill
  fitToWidth?: boolean
  placeholder?: React.ReactNode
}

export function TreePillsInput<T extends Record<string, any>>({
  tree,
  onCheckedStateChange,
  initialCheckedState,
  initialExpandedState,
  maxChars,
  maxPills = 5,
  fitToWidth = true,
  placeholder,
  nodeRenderer,
  ...props
}: TreePillsInputProps<T>) {
  if (maxPills !== undefined && maxChars !== undefined) {
    console.warn("if maxChars is set, maxPills does not have any effect")
  }
  const treeState = useTree({
    initialCheckedState: initialCheckedState ?? undefined,
    initialExpandedState: initialExpandedState ?? undefined,
  })

  React.useLayoutEffect(() => {
    debugLog("Initializing tree with:", tree)
    debugLog("Initial expanded state:", initialExpandedState)
    debugLog("Initial checked state:", initialCheckedState)
    debugLog("Tree state before initialization:", treeState)
    treeState.initialize(tree)

    if (initialExpandedState) {
      Object.entries(initialExpandedState).forEach(
        ([nodeValue, isExpanded]) => {
          if (isExpanded) {
            treeState.expand(nodeValue)
          } else {
            treeState.collapse(nodeValue)
          }
        }
      )
    }
  }, [tree])

  const [treeInitialised, setTreeInitialised] = React.useState(false)
  const previousSelectedRef = React.useRef<string[]>([])
  const previousExternalSelectionRef = React.useRef<string[] | undefined>(
    undefined
  )

  React.useEffect(() => {
    const nextValues = [...(initialCheckedState ?? [])].sort()

    if (
      previousExternalSelectionRef.current &&
      arraysAreEqual(previousExternalSelectionRef.current, nextValues)
    ) {
      return
    }

    previousExternalSelectionRef.current = nextValues
    const selectedValues = getSelectedNodes(tree, treeState.getCheckedNodes())
      .map((value) => value.value)
      .sort()

    if (!arraysAreEqual(selectedValues, nextValues)) {
      treeState.setCheckedState(getCheckedLeafValues(tree, nextValues))
      previousSelectedRef.current = nextValues
    }
  }, [initialCheckedState, tree, treeState])

  React.useEffect(() => {
    const selectedValues = getSelectedNodes(tree, treeState.getCheckedNodes())
      .map((v) => v.value)
      .sort()

    if (!treeInitialised) {
      const initialLeafValues = getCheckedLeafValues(
        tree,
        initialCheckedState ?? []
      ).sort()
      const selectedLeafValues = getCheckedLeafValues(
        tree,
        selectedValues
      ).sort()
      if (arraysAreEqual(selectedLeafValues, initialLeafValues)) {
        previousSelectedRef.current = selectedValues
        setTreeInitialised(true)
      }
      return
    }

    if (arraysAreEqual(previousSelectedRef.current, selectedValues)) {
      return
    }

    previousSelectedRef.current = selectedValues
    onCheckedStateChange?.(selectedValues)
  }, [
    tree,
    treeInitialised,
    treeState.checkedState,
    initialCheckedState,
    onCheckedStateChange,
  ])

  const values = getSelectedNodes(tree, treeState.getCheckedNodes())
  const maxPillCount =
    findMaxPillCountBasedOnValueLabelLength(maxChars, values) ?? maxPills

  return (
    <Popover disabled={props.disabled} position="bottom" withArrow shadow="md">
      <Popover.Target>
        <PillsInput
          pointer
          rightSectionPointerEvents={values?.length ? undefined : "none"}
          rightSection={
            values?.length ? (
              props.disabled ? null : (
                <ActionIcon
                  onClick={() =>
                    treeState
                      .getCheckedNodes()
                      .map((x) => treeState.uncheckNode(x.value))
                  }
                  size="xs"
                  variant="transparent"
                >
                  <IconX color="var(--mantine-color-gray-7)" size="16" />
                </ActionIcon>
              )
            ) : (
              <IconSelector
                color="var(--mantine-color-placeholder)"
                size="16"
              />
            )
          }
          styles={{ input: { alignContent: "end" } }}
          {...props}
          size="xs"
        >
          {values?.length ? (
            fitToWidth ? (
              <OverflowList
                data={values}
                maxVisibleItems={maxPillCount}
                renderItem={(item) => (
                  <Pill
                    key={item.value}
                    withRemoveButton={!props.disabled}
                    onRemove={() => treeState.uncheckNode(item.value)}
                  >
                    {item.label}
                  </Pill>
                )}
                renderOverflow={(items) => (
                  <HoverCard shadow="md" openDelay={100} closeDelay={100}>
                    <HoverCard.Target>
                      <Pill>+{items.length}</Pill>
                    </HoverCard.Target>
                    <HoverCard.Dropdown p="xs">
                      <Stack gap={6}>
                        {items.map((item) => (
                          <Pill
                            key={item.value}
                            withRemoveButton={!props.disabled}
                            onRemove={() => treeState.uncheckNode(item.value)}
                          >
                            {item.label}
                          </Pill>
                        ))}
                      </Stack>
                    </HoverCard.Dropdown>
                  </HoverCard>
                )}
              />
            ) : (
              <Pill.Group>
                {values
                  ?.map((v) => (
                    <Pill
                      key={v.value}
                      withRemoveButton={!props.disabled}
                      onRemove={() => treeState.uncheckNode(v.value)}
                    >
                      {v.label}
                    </Pill>
                  ))
                  .slice(0, maxPillCount)}
                {maxPillCount !== undefined &&
                values !== undefined &&
                values.length > maxPillCount ? (
                  <Pill>+{values.length - maxPillCount}</Pill>
                ) : null}
              </Pill.Group>
            )
          ) : (
            <Text size="sm" c="var(--mantine-color-placeholder)">
              {placeholder}
            </Text>
          )}
        </PillsInput>
      </Popover.Target>
      <Popover.Dropdown w={props.w} maw={props.mah} p={10}>
        <ScrollContainer mah={400}>
          <TreeRenderer
            tree={tree}
            treeState={treeState}
            nodeRenderer={nodeRenderer}
          />
        </ScrollContainer>
      </Popover.Dropdown>
    </Popover>
  )
}
